import DOMPurify from "dompurify";

/**
 * Allowed HTML tags that CKEditor legitimately produces.
 * Everything else (script, iframe, object, embed, form, etc.) is stripped.
 */
const ALLOWED_TAGS = [
    "p", "br", "strong", "b", "em", "i", "u", "s",
    "a",
    "ul", "ol", "li",
    "blockquote",
    "h2", "h3", "h4",
    "table", "thead", "tbody", "tr", "td", "th",
    "figure", "figcaption",
];

/**
 * Allowed attributes — only safe, non-event attributes.
 * href is allowed on <a> tags; target and rel are kept for "open in new tab".
 */
const ALLOWED_ATTR = ["href", "target", "rel"];

/**
 * Sanitize rich-text HTML from CKEditor before sending to the API.
 * Removes <script>, <iframe>, onerror=, onclick=, javascript: URIs, etc.
 *
 * @param {string} html - Raw HTML string from CKEditor
 * @returns {string} - Sanitized HTML safe for storage and rendering
 */
export const sanitizeHtml = (html) => {
    if (!html) return html;

    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
    });
};

/**
 * CKEditor onReady handler that sanitizes pasted / dropped HTML in real-time.
 * Attach via: <CKEditor onReady={sanitizeCKEditorOnReady} ... />
 *
 * Intercepts paste at the DOM level (capture phase) BEFORE CKEditor processes
 * the HTML, so dangerous tags like <img onerror="..."> never reach the editor.
 */
export const sanitizeCKEditorOnReady = (editor) => {
    const editableElement = editor.ui.getEditableElement();
    if (!editableElement) return;

    editableElement.addEventListener(
        "paste",
        (event) => {
            const html = event.clipboardData?.getData("text/html");
            if (!html) return;

            const clean = sanitizeHtml(html);
            if (clean === html) return; // nothing dangerous — let CKEditor handle normally

            event.preventDefault();
            event.stopImmediatePropagation();

            const viewFragment = editor.data.processor.toView(clean);
            const modelFragment = editor.data.toModel(viewFragment);
            editor.model.insertContent(modelFragment);
        },
        true // capture phase — runs before CKEditor's own listeners
    );
};
